import type { SemanticAnalysis, SemanticElements, VisualInterpretation } from './types';

// Maps that expand keywords into vivid VISUAL descriptions — no camera/render jargon
const SUBJECT_VISUAL_MAP: Array<[RegExp, string]> = [
  [/\b(man|boy|male|رجل|ولد)\b/i, 'a young man with defined features'],
  [/\b(woman|girl|female|امرأة|بنت)\b/i, 'a young woman with expressive features'],
  [/\b(warrior|soldier|محارب)\b/i, 'a warrior in detailed armor'],
  [/\b(cat|قطة)\b/i, 'a fluffy cat with bright expressive eyes'],
  [/\b(dog|كلب)\b/i, 'a dog with soft natural fur'],
  [/\b(robot|روبوت)\b/i, 'a sleek futuristic robot with metallic surfaces'],
];

const ACTION_VISUAL_MAP: Array<[RegExp, string]> = [
  [/\b(standing|واقف)\b/i, 'standing confidently'],
  [/\b(sitting|جالس)\b/i, 'sitting in a relaxed pose'],
  [/\b(walking|يمشي)\b/i, 'walking forward with purpose'],
  [/\b(running|يركض)\b/i, 'running with dynamic energy, slight motion blur'],
  [/\b(dancing|يرقص)\b/i, 'mid-dance with flowing movement'],
  [/\b(play|يلعب|تلعب)\b/i, 'playfully interacting'],
];

const ENVIRONMENT_VISUAL_MAP: Array<[RegExp, string]> = [
  [/\b(street|city|شارع|مدينة)\b/i, 'on a city street with buildings in the background'],
  [/\b(rooftop|سطح)\b/i, 'on a rooftop overlooking the city skyline'],
  [/\b(desert|صحراء)\b/i, 'in a vast desert with sand dunes stretching to the horizon'],
  [/\b(forest|غابة|jungle)\b/i, 'in a lush forest with dappled sunlight through leaves'],
  [/\b(beach|ocean|شاطئ|بحر)\b/i, 'on a beach with waves and wet sand reflections'],
  [/\b(room|office|غرفة|مكتب)\b/i, 'in a cozy interior space with warm ambient light'],
];

const TIME_LIGHTING_MAP: Array<[RegExp, string]> = [
  [/\b(night|ليل|moonlight|قمر)\b/i, 'at night with cool blue moonlight and soft shadows'],
  [/\b(sunset|golden hour|غروب)\b/i, 'during golden hour with warm orange-gold light'],
  [/\b(sunrise|فجر)\b/i, 'at dawn with soft pink and lavender light'],
  [/\b(rain|مطر)\b/i, 'during rain with wet reflections and moody atmosphere'],
  [/\b(fog|mist|ضباب)\b/i, 'in misty conditions with soft diffused light'],
  [/\b(neon)\b/i, 'with colorful neon lights casting vibrant reflections'],
];

const MOOD_STYLE_MAP: Array<[RegExp, string]> = [
  [/\b(cinematic|سينمائي)\b/i, 'cinematic look with rich color grading'],
  [/\b(dramatic|درامي|epic|ملحمي)\b/i, 'dramatic atmosphere with high contrast'],
  [/\b(photorealistic|realistic|واقعي)\b/i, 'photorealistic style with natural details'],
  [/\b(anime|أنمي)\b/i, 'anime art style with clean lines and vibrant colors'],
  [/\b(cyberpunk)\b/i, 'cyberpunk aesthetic with neon and dark tones'],
  [/\b(vintage|كلاسيكي)\b/i, 'vintage look with warm tones and subtle grain'],
];

const mapVisual = (
  source: string,
  mapping: Array<[RegExp, string]>,
  fallback: string,
): string => {
  for (const [pattern, replacement] of mapping) {
    if (pattern.test(source)) {
      return replacement;
    }
  }
  return fallback;
};

const mergeFallback = (value: string, fallback: string): string => {
  const clean = value.trim();
  return clean || fallback;
};

const expandSubject = (value: string): string =>
  mapVisual(value, SUBJECT_VISUAL_MAP, mergeFallback(value, 'a subject'));

const expandAction = (value: string): string =>
  mapVisual(value, ACTION_VISUAL_MAP, mergeFallback(value, ''));

const expandEnvironment = (value: string): string =>
  mapVisual(value, ENVIRONMENT_VISUAL_MAP, mergeFallback(value, 'in a natural setting'));

const expandTimeLighting = (value: string): string =>
  mapVisual(value, TIME_LIGHTING_MAP, mergeFallback(value, 'with soft natural lighting'));

const expandMoodStyle = (value: string): string =>
  mapVisual(value, MOOD_STYLE_MAP, mergeFallback(value, 'photorealistic style'));

export const interpretVisuals = (semantic: SemanticAnalysis): VisualInterpretation => {
  const { elements } = semantic;
  return {
    subject: expandSubject(elements.subject),
    action: expandAction(elements.action),
    environment: expandEnvironment(elements.environment),
    timeLighting: expandTimeLighting(elements.timeLighting),
    moodStyle: expandMoodStyle(elements.moodStyle),
  };
};

export const interpretVisualsFromElements = (elements: SemanticElements): VisualInterpretation => {
  return {
    subject: expandSubject(elements.subject),
    action: expandAction(elements.action),
    environment: expandEnvironment(elements.environment),
    timeLighting: expandTimeLighting(elements.timeLighting),
    moodStyle: expandMoodStyle(elements.moodStyle),
  };
};
